import { inject, Injectable, Injector } from '@angular/core';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { AppConfigService } from '../app-config.service';
import { AuthProvider } from './auth.provider';
import { KeycloakAuthProvider } from './keycloak.provider';
import { Router } from '@angular/router';
import { IOidcClientConfig, IAuthModuleConfig, AuthClientKey } from '@web/dtos';
import { NamespacedOAuthStorage } from './namespaced-oauth-storage';

@Injectable({ providedIn: 'root' })
export class AuthProviderFactory {
  private readonly router: Router = inject(Router);

  private readonly providers: Map<AuthClientKey, AuthProvider> = new Map<
    AuthClientKey,
    AuthProvider
  >();

  constructor(
    private configService: AppConfigService,
    private injector: Injector
  ) {}

  getProvider(clientKey: AuthClientKey): AuthProvider {
    const existingProvider = this.providers.get(clientKey);
    if (existingProvider) {
      return existingProvider;
    }

    const moduleConfig = this.configService.get().auth;
    const clientConfig = moduleConfig.clients[clientKey];

    if (!clientConfig) {
      throw new Error(
        `[AuthProviderFactory] Kein Config-Eintrag für Client: "${clientKey}"`
      );
    }

    const provider = this.createProvider(clientKey, moduleConfig, clientConfig);
    this.providers.set(clientKey, provider);
    return provider;
  }

  private createProvider(
    clientKey: AuthClientKey,
    moduleConfig: IAuthModuleConfig,
    config: IOidcClientConfig
  ): AuthProvider {
    switch (moduleConfig.provider) {
      case 'keycloak':
        return new KeycloakAuthProvider(
          this.createOAuthService(clientKey),
          config,
          this.router,
          clientKey
        );
      default:
        throw new Error(`Unknown auth provider: ${moduleConfig.provider}`);
    }
  }

  private createOAuthService(clientKey: AuthClientKey): OAuthService {
    return Injector.create({
      providers: [
        {
          provide: OAuthStorage,
          useValue: new NamespacedOAuthStorage(`auth.${clientKey}`),
        },
        { provide: OAuthService, useClass: OAuthService },
      ],
      parent: this.injector,
    }).get(OAuthService);
  }
}
